/** Helpers for the GEOFENCE_OVERRIDE_REQUIRED structured error. */

export interface GeofenceErrorDetails {
  closestLocationId: string | null;
  closestLocationName: string | null;
  closestDistanceM: number | null;
  closestRadiusM: number | null;
}

export interface NormalizedApiError {
  code?: string;
  title?: string;
  detail?: string;
  fieldErrors?: Record<string, string | string[]> | string[];
  traceId?: string;
  status?: number;
}

function pickFirst(value: string | string[] | undefined): string | null {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
}

/** Returns details when error is a GEOFENCE_OVERRIDE_REQUIRED, else null. */
export function asGeofenceError(e: unknown): GeofenceErrorDetails | null {
  const err = e as NormalizedApiError;
  if (!err || err.code !== "GEOFENCE_OVERRIDE_REQUIRED") return null;
  const fe = err.fieldErrors;
  if (!fe || Array.isArray(fe)) {
    return {
      closestLocationId: null,
      closestLocationName: null,
      closestDistanceM: null,
      closestRadiusM: null,
    };
  }
  const distance = pickFirst(fe.closestDistanceM as string | string[] | undefined);
  const radius = pickFirst(fe.closestRadiusM as string | string[] | undefined);
  return {
    closestLocationId: pickFirst(fe.closestLocationId as string | string[] | undefined),
    closestLocationName: pickFirst(fe.closestLocationName as string | string[] | undefined),
    closestDistanceM: distance != null ? Number(distance) : null,
    closestRadiusM: radius != null ? Number(radius) : null,
  };
}

export interface AmbiguousCandidate {
  locationId: string;
  name: string;
  distanceM: number;
  radiusM: number;
  inside?: boolean;
  isPrimary?: boolean;
}

const EARTH_RADIUS_M = 6371000;

export function haversineM(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const p1 = toRad(a.lat);
  const p2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Returns candidate list when error is GEOFENCE_AMBIGUOUS, else null. */
export function asGeofenceAmbiguousError(e: unknown): AmbiguousCandidate[] | null {
  const err = e as NormalizedApiError;
  if (!err || err.code !== "GEOFENCE_AMBIGUOUS") return null;
  const fe = err.fieldErrors;
  if (!fe || Array.isArray(fe)) return [];
  const raw = (fe as Record<string, unknown>).candidates;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      const o = c as Record<string, unknown>;
      const id = o.locationId;
      const name = o.name;
      if (typeof id !== "string" || typeof name !== "string") return null;
      return {
        locationId: id,
        name,
        distanceM: Number(o.distanceM ?? 0),
        radiusM: Number(o.radiusM ?? 0),
      };
    })
    .filter((c): c is AmbiguousCandidate => c !== null);
}
